import { useMemo, useState } from "react";
import {
	useTanstackTable,
	createColumnHelper,
	EMPTY_ARRAY,
} from "@/hooks/useTanstackTable";
import { useApi } from "@/hooks/useApi";
import type { Empresa } from "@/@types/Api_Extracted";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/datatable";
import { Dialog, DialogTrigger } from "../ui/dialog";
import { DialogEditEmpresa } from "./EmpresaDialog";
import {
	ColumnFiltersState,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
} from "@tanstack/react-table";

export function UserListTable() {
	const api = useApi();
	const query = api.useQuery("get", "/v1/Admin/Empresas", {});

	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const columns = useMemo(() => Columns(query.refetch), [query.refetch]);
	const table = useTanstackTable({
		columns,
		data: query.data ?? EMPTY_ARRAY,
		filterFns: {},
		state: {
			columnFilters,
		},
		onColumnFiltersChange: setColumnFilters,
		getFilteredRowModel: getFilteredRowModel(), //client side filtering
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
	});

	return (
		<>
			<DataTable
				table={table}
				noResultsMessage="Aún no hay imputaciones registradas"
				errorMessage="Error al cargar las imputaciones"
				isLoading={query.isPending}
				isError={query.isError}
			/>
		</>
	);
}

function Columns(onSuccess: () => void) {
	const columnHelper = createColumnHelper<Empresa>();

	return [
		columnHelper.accessor("idUsuario", {
			header: "ID",
			cell: (props) => props.renderValue(),
			enableSorting: false,
			enableColumnFilter: false,
		}),
		columnHelper.accessor("razonSocial", {
			header: "Razón Social",
			cell: (props) => props.renderValue(),
			meta: {
				filterVariant: "text",
			},
		}),
		columnHelper.accessor("username", {
			header: "Email",
			cell: (props) => props.renderValue(),
			meta: {
				filterVariant: "text",
			},
		}),
		columnHelper.accessor("estado", {
			header: "Estado",
			cell: (props) => localizedStatus[props.getValue()],
			meta: {
				filterVariant: "select",
				filterOptions: localizedStatus,
			},
		}),
		columnHelper.display({
			id: "acciones",
			header: "Acciones",
			cell: (props) => (
				<AccionesFila fila={props.row.original} onSuccess={onSuccess} />
			),
		}),
	];
}

function AccionesFila(props: { fila: Empresa; onSuccess: () => void }) {
	const [open, setOpen] = useState(false);
	const onSuccess = () => {
		setOpen(false);
		props.onSuccess();
	};

	return (
		<div className="grid grid-cols-1 gap-2">
			<Dialog onOpenChange={setOpen} open={open}>
				<DialogTrigger asChild>
					<Button variant="secondary">Editar</Button>
				</DialogTrigger>
				<DialogEditEmpresa empresa={props.fila} onSuccess={onSuccess} />
			</Dialog>
		</div>
	);
}

const localizedStatus = {
	PENDING: "Pendiente",
	ACTIVE: "Activo",
	INACTIVE: "Inactivo",
};
